import {
  Add,
  Image as ImageIcon,
  MoreVert,
  RefreshOutlined,
} from "@mui/icons-material";
import {
  Avatar,
  AvatarGroup,
  Box,
  Button,
  IconButton,
  LinearProgress,
  Menu,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import axios from "axios";
import { User } from "../../utils";
import moment from "moment";
import React, { useEffect, useState } from "react";
import { Company } from "../../types";
import { hostname, parseError } from "../../utils";
import BrandImageDialog from "./BrandImageDialog";

// Brand Image List Component
export default ({
  currentUser,
  getToken,
  openDialog,
  eventListener,
  companyId,
  company,
  currentPlan,
}: {
  getToken: Function;
  openDialog: Function;
  eventListener: Function;
  currentUser: any | User;
  companyId: string;
  company: Company;
  currentPlan: string;
}) => {
  const [editorOpen, setEditorOpen] = useState(false);
  const [brandImages, setBrandImages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedImage, setSelectedImage] = useState<any>(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedId, setSelectedId] = useState<any>(null);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [totalImages, setTotalImages] = useState(0);

  // Fetch images when component mounts
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await fetchImages();
      setLoading(false);
    };
    loadData();
  }, [companyId, page, pageSize]);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setPageSize(parseInt(event.target.value, 10));
    setPage(0);
  };

  const fetchImages = async () => {
    setRefreshing(true);
    try {
      const token = await getToken();
      const response = await axios.get(`${hostname}/content/brand-images`, {
        params: { companyId, page: page + 1, pageSize },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.data) {
        setBrandImages(response.data.items || []);
        setTotalImages(response.data.total || 0);
      }
    } catch (error) {
      console.error("Error fetching brand images:", error);
      eventListener({
        type: "SET_ERROR",
        error: parseError(error),
      });
    } finally {
      setRefreshing(false);
    }
  };

  const handleSaveImage = async (imageData: any) => {
    setLoading(true);
    try {
      const token = await getToken();
      const response = await axios.post(
        `${hostname}/content/brand-images`,
        {
          companyId,
          image: imageData,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      // Refresh the images list
      await fetchImages();
      setEditorOpen(false);
      setSelectedImage(null);
    } catch (error) {
      console.error("Error saving brand image:", error);
      eventListener({
        type: "SET_ERROR",
        error: parseError(error),
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteImage = async (imageId: string) => {
    openDialog(
      "Delete Brand Image",
      `Are you sure you want to delete this brand image record?`,
      "Confirm",
      async () => {
        setLoading(true);
        try {
          const token = await getToken();
          await axios.delete(`${hostname}/content/brand-images/${imageId}`, {
            params: { companyId },
            headers: {
              Authorization: `Bearer ${token}`,
            },
          });

          // Refresh the images list
          await fetchImages();
        } catch (error) {
          console.error("Error deleting brand image:", error);
          eventListener({
            type: "SET_ERROR",
            error: parseError(error),
          });
        } finally {
          setLoading(false);
        }
      }
    );
  };

  const handleEditImage = (image: any) => {
    setSelectedImage(image);
    setEditorOpen(true);
  };

  // Main Return
  return (
    <Box sx={{ height: "100%", width: "100%", mt: 4 }}>
      {loading && <LinearProgress sx={{ mt: 2 }} />}

      {!loading && (
        <>
          <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
            <Box>
              <Button
                variant="contained"
                color="primary"
                onClick={() => {
                  setSelectedImage(null);
                  setEditorOpen(true);
                }}
                startIcon={<Add />}
              >
                New Brand Image
              </Button>
            </Box>
          </Box>

          {brandImages.length === 0 ? (
            <Paper sx={{ p: 4, textAlign: "center" }}>
              <ImageIcon
                sx={{ fontSize: 64, color: "text.secondary", mb: 2 }}
              />
              <Typography variant="h6" gutterBottom>
                No Brand Images Yet
              </Typography>
              <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                Create brand image collections to use as references when
                generating content for campaigns. Each brand image can hold
                multiple images and be assigned to specific campaigns.
              </Typography>
              <Button
                variant="contained"
                color="primary"
                onClick={() => {
                  setSelectedImage(null);
                  setEditorOpen(true);
                }}
                startIcon={<Add />}
              >
                Create First Brand Image
              </Button>
            </Paper>
          ) : (
            <Paper
              style={{ marginTop: 20, marginBottom: 40 }}
              variant="outlined"
            >
              {refreshing && <LinearProgress />}
              {!refreshing && <div style={{ height: 4 }}></div>}
              <IconButton
                onClick={() => fetchImages()}
                style={{ float: "right", marginRight: 5 }}
              >
                <RefreshOutlined />
              </IconButton>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Images</TableCell>
                      <TableCell>Name</TableCell>
                      <TableCell>Description</TableCell>
                      <TableCell>AI Models</TableCell>
                      <TableCell>Created</TableCell>
                      <TableCell></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {brandImages.map((brandImage: any) => (
                      <TableRow key={brandImage.id} hover>
                        <TableCell>
                          {brandImage.images && brandImage.images.length > 0 ? (
                            <Tooltip
                              title={`${brandImage.images.length} image${
                                brandImage.images.length > 1 ? "s" : ""
                              }`}
                            >
                              <AvatarGroup
                                max={4}
                                sx={{
                                  justifyContent: "flex-end",
                                  "& .MuiAvatar-root": { width: 36, height: 36 },
                                }}
                              >
                                {brandImage.images.map(
                                  (img: any, idx: number) => (
                                    <Avatar
                                      key={idx}
                                      src={img.url}
                                      alt={img.type || "Image"}
                                    />
                                  )
                                )}
                              </AvatarGroup>
                            </Tooltip>
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              No images
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body1" fontWeight="medium">
                            {brandImage.name}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{
                              maxWidth: 200,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {brandImage.description || "-"}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            {brandImage.imageModels &&
                            brandImage.imageModels.length > 0
                              ? `${brandImage.imageModels.length} model${
                                  brandImage.imageModels.length > 1 ? "s" : ""
                                }`
                              : "Any"}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {brandImage.created_date
                            ? moment(brandImage.created_date).format(
                                "MMM D, YYYY"
                              )
                            : "-"}
                        </TableCell>
                        <TableCell>
                          <IconButton
                            onClick={(e: any) => {
                              e.stopPropagation();
                              setAnchorEl(e.currentTarget);
                              setSelectedId(brandImage.id);
                            }}
                          >
                            <MoreVert />
                          </IconButton>
                          <Menu
                            anchorEl={anchorEl}
                            open={selectedId === brandImage.id}
                            onClose={(e: any) => {
                              e.stopPropagation();
                              setAnchorEl(null);
                              setSelectedId(null);
                            }}
                          >
                            <MenuItem
                              onClick={async (e) => {
                                e.stopPropagation();
                                handleEditImage(brandImage);
                                setAnchorEl(null);
                                setSelectedId(null);
                              }}
                            >
                              Edit
                            </MenuItem>
                            <MenuItem
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteImage(brandImage.id);
                                setAnchorEl(null);
                                setSelectedId(null);
                              }}
                            >
                              Delete
                            </MenuItem>
                          </Menu>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                <TablePagination
                  rowsPerPageOptions={[5, 10, 25, 50]}
                  component="div"
                  count={totalImages}
                  rowsPerPage={pageSize}
                  page={page}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                />
              </TableContainer>
            </Paper>
          )}
        </>
      )}

      {/* Create/Edit Dialog */}
      <BrandImageDialog
        open={editorOpen}
        onClose={() => {
          setEditorOpen(false);
          setSelectedImage(null);
        }}
        companyId={companyId}
        company={company}
        onSave={handleSaveImage}
        existingData={selectedImage}
        loading={loading}
        getToken={getToken}
        eventListener={eventListener}
      />
    </Box>
  );
};
